import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSettings } from "../state/settings";
import { useWorkspaces } from "../state/workspaces";
import { useUi } from "../state/ui";
import { texte } from "../texte";
import { toast } from "../notify";
import * as api from "../api/client";
import type { ApiCtx } from "../api/client";
import type { CloudConfig } from "../api/types";
import { uploadInBatches } from "../convert/chunker";
import { uploadLargeFile } from "../files/upload";
import { convLabelFor, convertFilesForUpload } from "../files/convert";

type PendingFile = { id: string; name: string; convLabel: string; file: File };

const EXPERT_KEYS: Array<{ key: keyof CloudConfig; def: string; numeric?: boolean }> = [
  { key: "model", def: "gpt-5.6-terra:low" },
  { key: "subagent_model", def: "gpt-5.6-luna:low" },
  { key: "budget", def: "150000", numeric: true },
  { key: "max_iterations", def: "8", numeric: true },
  { key: "max_parallel", def: "3", numeric: true },
  { key: "language", def: "en" },
  { key: "plan", def: "false" },
  { key: "planner_model", def: "gpt-5.6-sol:medium" },
  { key: "effort_models", def: '{"high": "gpt-5.6-sol", "medium": "gpt-5.6-terra", "low": "gpt-5.6-luna"}' },
  { key: "max_plan_depth", def: "2", numeric: true },
  { key: "max_retries", def: "3", numeric: true },
  { key: "max_refinements", def: "3", numeric: true },
  { key: "critique", def: "false" },
  { key: "critique_model", def: "gpt-5.6-terra:low" },
  { key: "critique_rounds", def: "2", numeric: true },
  { key: "python", def: "true" },
  { key: "image", def: "false" },
  { key: "image_model", def: "gpt-5-mini" },
];

export function Wizard() {
  const navigate = useNavigate();
  const apiUrl = useSettings((s) => s.apiUrl);
  const s3Prefix = useSettings((s) => s.s3Prefix);
  const byName = useWorkspaces((s) => s.byName);
  const upsert = useWorkspaces((s) => s.upsert);
  const sessionView = useUi((s) => s.sessionView);

  const existingAdminToken = Object.values(byName).find((w) => w.role === "admin")?.token;

  const [step, setStep] = useState(1);
  const [name, setName] = useState("pyanalysis1-kurs3");
  const [bootstrapToken, setBootstrapToken] = useState("");
  const [files, setFiles] = useState<PendingFile[]>([]);
  const [pdfAsImages, setPdfAsImages] = useState(false);
  const [task, setTask] = useState(
    "Analysiere die Verkaufsdaten im Dokument 'sales.csv'. Schreibe ein Python-Skript, um den Gesamtumsatz der " +
      "Woche, die insgesamt verkauften Einheiten sowie eine Tabelle/Liste des Umsatzes pro Produkt (absteigend " +
      "sortiert) zu berechnen. Führe das Skript mit dem run_python Tool aus. Schreibe die Ergebnisse in ein " +
      "Dokument namens 'summary.md' in den Workspace. Nutze KEINE Schätzungen des Sprachmodells für die " +
      "Berechnungen, sondern verlasse dich vollständig auf die Ergebnisse der Python-Ausführung.",
  );
  const [budget, setBudget] = useState(150000);
  const [simpleToggles, setSimpleToggles] = useState({ plan: false, python: true, image: false, critique: false });
  const [expertValues, setExpertValues] = useState<Record<string, string>>(
    Object.fromEntries(EXPERT_KEYS.map((e) => [e.key, e.def])),
  );
  const [busy, setBusy] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);

  const isSimple = sessionView === "simple";

  function addFiles(list: FileList | File[]) {
    const arr = Array.from(list);
    setFiles((prev) => [
      ...prev,
      ...arr.map((file) => ({
        id: `${file.name}-${file.size}-${Math.random().toString(36).slice(2)}`,
        name: file.name,
        convLabel: convLabelFor(file, pdfAsImages),
        file,
      })),
    ]);
  }

  function removeFile(id: string) {
    setFiles((prev) => prev.filter((f) => f.id !== id));
  }

  function buildConfig(): CloudConfig {
    if (isSimple) {
      return {
        budget,
        plan: simpleToggles.plan,
        python: simpleToggles.python,
        image: simpleToggles.image,
        critique: simpleToggles.critique,
      };
    }
    const out: CloudConfig = {};
    for (const { key, numeric } of EXPERT_KEYS) {
      const raw = expertValues[key];
      if (raw === "true" || raw === "false") (out as Record<string, unknown>)[key] = raw === "true";
      else if (numeric) (out as Record<string, unknown>)[key] = Number(raw);
      else if (key === "effort_models") {
        // Server erwartet ein Objekt {high, medium, low} (validate_config), kein
        // Freitext — hier als JSON eingegeben, sonst greift die Server-Validierung.
        try {
          (out as Record<string, unknown>)[key] = JSON.parse(raw);
        } catch {
          (out as Record<string, unknown>)[key] = raw;
        }
      } else (out as Record<string, unknown>)[key] = raw;
    }
    return out;
  }

  async function finish() {
    const cleanName = name.trim().toLowerCase().replace(/[^a-z0-9-]/g, "-") || "arbeitsbereich";
    const finalName = byName[cleanName] ? `${cleanName}-2` : cleanName;
    const token = existingAdminToken || bootstrapToken.trim();
    if (!token) {
      toast(texte.errors.generic, "Bitte zunächst ein Zugangstoken eingeben.");
      return;
    }
    const ctx: ApiCtx = { apiUrl, token, workspace: finalName };
    const config = buildConfig();
    console.info("[Wizard] finish", { workspace: finalName, fileCount: files.length, config });

    setBusy("Dateien werden hochgeladen…");
    try {
      const { smallDocs, smallImages, largeFiles } = await convertFilesForUpload(
        files.map((pf) => pf.file),
        pdfAsImages,
      );
      console.info("[Wizard] converted files", {
        smallDocs: smallDocs.length,
        smallImages: smallImages.length,
        largeFiles: largeFiles.length,
      });

      const { errors } = await uploadInBatches(ctx, smallDocs, smallImages, (p) =>
        setBusy(`Batch ${p.batch}/${p.totalBatches} · ${p.count} Dokumente…`),
      );
      for (const file of largeFiles) {
        setBusy(`Direkt-Upload: ${file.name}…`);
        await uploadLargeFile(s3Prefix, finalName, file);
      }
      if (errors.length) {
        console.error("[Wizard] upload errors", errors);
        toast(texte.errors.generic, `${errors.length} Dokument(e) fehlgeschlagen: ${errors.map((e) => e.doc_id).join(", ")}`);
      } else {
        console.info("[Wizard] upload complete", { written: smallDocs.length + smallImages.length + largeFiles.length });
      }

      // Legt den Arbeitsbereich an (Konfig + Aufgabe als goal.md) OHNE einen Lauf zu
      // starten — "Starten" bzw. "Plan erstellen" ist ein separater, späterer Schritt
      // auf der Workspace-Seite (dort auch für Lernende in der Einfach-Sicht erreichbar).
      setBusy("Arbeitsbereich wird angelegt…");
      const res = await api.create(ctx, task, config);
      console.info("[Wizard] create result", res);
      if (res.code < 0) {
        console.error("[Wizard] create failed", res.msg);
        toast(texte.errors.generic, res.msg ?? texte.errors.generic);
        setBusy(null);
        return;
      }

      upsert({
        workspace: finalName,
        token,
        role: "admin",
        view: sessionView,
        origin: "created",
        addedAt: Date.now(),
        notifiedQids: [],
        expertEnabled: false,
      });
      navigate(`/ws/${token}/${encodeURIComponent(finalName)}`);
    } catch (e) {
      console.error("[Wizard] finish failed", e);
      toast(texte.errors.generic, e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  }

  const steps = texte.wizard.steps;

  return (
    <main className="page">
      <div className="wizard">
        <div className="page-head">
          <h1>{texte.wizard.title}</h1>
        </div>
        <div className="steps">
          {steps.map((label, i) => (
            <div key={label} className={`step ${step === i + 1 ? "on" : step > i + 1 ? "done" : ""}`}>
              <div className="bar" />
              {i + 1}. {label}
            </div>
          ))}
        </div>
        <div className="card">
          {step === 1 && (
            <>
              <h2>{texte.wizard.step1Title}</h2>
              <p className="sub">{texte.wizard.step1Sub}</p>
              {!existingAdminToken && (
                <div className="field">
                  <span className="label">Zugangstoken (einmalig für diesen Browser)</span>
                  <input
                    type="text"
                    value={bootstrapToken}
                    onChange={(e) => setBootstrapToken(e.target.value)}
                    placeholder="anna_ab12cd34ef_1752300000"
                    style={{ fontFamily: "var(--mono)" }}
                  />
                </div>
              )}
              <div className="field">
                <span className="label">{texte.wizard.workspaceNameLabel}</span>
                <input type="text" value={name} onChange={(e) => setName(e.target.value)} />
              </div>
              <div className="field">
                <span className="label">{texte.wizard.filesLabel}</span>
                <div
                  className={`dropzone${dragOver ? " over" : ""}`}
                  role="button"
                  tabIndex={0}
                  onClick={() => fileInputRef.current?.click()}
                  onKeyDown={(e) => e.key === "Enter" && fileInputRef.current?.click()}
                  onDragOver={(e) => {
                    e.preventDefault();
                    setDragOver(true);
                  }}
                  onDragLeave={() => setDragOver(false)}
                  onDrop={(e) => {
                    e.preventDefault();
                    setDragOver(false);
                    if (e.dataTransfer.files.length) addFiles(e.dataTransfer.files);
                  }}
                >
                  {texte.wizard.dropzoneText}
                  <br />
                  <span className="hint">{texte.wizard.dropzoneHint}</span>
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  style={{ display: "none" }}
                  onChange={(e) => e.target.files && addFiles(e.target.files)}
                />
                <label className="toggle-row" style={{ marginTop: 10 }}>
                  <span className="txt">
                    <b>PDFs als Bildseiten verarbeiten</b>
                    <span className="hint">Statt Textextraktion: ein PNG pro Seite (für Bilderkennungs-Aufgaben).</span>
                  </span>
                  <span className="switch">
                    <input type="checkbox" checked={pdfAsImages} onChange={(e) => setPdfAsImages(e.target.checked)} />
                    <span className="tr" />
                  </span>
                </label>
                <div className="chips">
                  {files.map((f) => (
                    <span className="chip" key={f.id}>
                      📄 {f.name} <span className="conv">→ {f.convLabel}</span>
                      <button aria-label={texte.wizard.remove} onClick={() => removeFile(f.id)}>
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            </>
          )}

          {step === 2 && (
            <>
              <h2>{texte.wizard.step2Title}</h2>
              <p className="sub">{texte.wizard.step2Sub}</p>
              <div className="field">
                <span className="label">{texte.wizard.taskLabel}</span>
                <textarea value={task} onChange={(e) => setTask(e.target.value)} />
              </div>
            </>
          )}

          {step === 3 &&
            (isSimple ? (
              <>
                <h2>{texte.wizard.step3TitleSimple}</h2>
                <p className="sub">{texte.wizard.step3SubSimple}</p>
                <div className="field">
                  <span className="label">{texte.wizard.budgetLabel}</span>
                  <div className="range-row">
                    <input
                      type="range"
                      min={50000}
                      max={500000}
                      step={10000}
                      value={budget}
                      onChange={(e) => setBudget(Number(e.target.value))}
                    />
                    <output className="num">{budget.toLocaleString("de-DE")} Tokens</output>
                  </div>
                  <span className="hint">{texte.wizard.budgetHint}</span>
                </div>
                {texte.wizard.toggles.map((t) => (
                  <div className="toggle-row" key={t.key}>
                    <span className="txt">
                      <b>{t.label}</b>
                      <span className="hint">{t.hint}</span>
                    </span>
                    <span className="switch">
                      <input
                        type="checkbox"
                        checked={simpleToggles[t.key as keyof typeof simpleToggles]}
                        onChange={(e) =>
                          setSimpleToggles((prev) => ({ ...prev, [t.key]: e.target.checked }))
                        }
                        aria-label={t.label}
                      />
                      <span className="tr" />
                    </span>
                  </div>
                ))}
              </>
            ) : (
              <>
                <h2>{texte.wizard.step3TitleExpert}</h2>
                <p className="sub">{texte.wizard.step3SubExpert}</p>
                <div className="cfg-grid">
                  {EXPERT_KEYS.map(({ key }) => (
                    <div className="field" key={key}>
                      <span className="label" style={{ textTransform: "none", fontFamily: "var(--mono)" }}>
                        {key}
                      </span>
                      <input
                        type="text"
                        value={expertValues[key]}
                        onChange={(e) => setExpertValues((prev) => ({ ...prev, [key]: e.target.value }))}
                      />
                    </div>
                  ))}
                </div>
              </>
            ))}

          <div className="nav">
            <button className="btn ghost" onClick={() => (step === 1 ? navigate("/") : setStep((s) => s - 1))}>
              {step === 1 ? texte.wizard.cancel : texte.wizard.back}
            </button>
            {step < 3 ? (
              <button className="btn primary" onClick={() => setStep((s) => s + 1)}>
                {texte.wizard.next}
              </button>
            ) : (
              <button className="btn primary" disabled={!!busy} onClick={finish}>
                {busy ?? texte.wizard.finishCreate}
              </button>
            )}
          </div>
          {step === 3 && !busy && <p className="hint" style={{ marginTop: 10 }}>{texte.wizard.finishHint}</p>}
        </div>
      </div>
    </main>
  );
}
